import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { AudiencePicker } from "@/admin/AudiencePicker";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, BookOpen, Users, MoreVertical, Pencil, Copy, Send, Archive, Trash2,
  ArchiveRestore, EyeOff, ArrowUp, ArrowDown,
} from "lucide-react";

const STATUS_STYLE = {
  published: "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]",
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  archived: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] line-through",
};

const blank = () => ({ category: "General", title: "", content: "", cta_label: "", cta_url: "", audience_spec: { type: "EVERYONE" } });

export default function KnowBeforeAdmin() {
  const { eventId } = useAdminEvent();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.mgrKbyg(eventId).then((r) => setItems(r.items || [])).catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await act(() => api.mgrKbReorder(eventId, arr.map((x) => x.id)), "Reordered");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <PageIntro>Know Before You Go is structured into attendee-friendly sections. Group-specific travel & guest policies are personalized by the shared audience engine, so PC/MDC, Leadership/Non-PC and Sprint-to-PC attendees each see only the policy that applies to them.</PageIntro>
        <Button data-testid="add-kb-btn" onClick={() => setEditing(blank())}><Plus className="h-4 w-4 mr-1" /> Add Article</Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="premium-card p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No articles yet.</div>}
        {items.map((k, idx) => (
          <div key={k.id} className="premium-card p-4" data-testid={`kb-row-${k.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex flex-col items-center gap-0.5 pt-0.5">
                  <button className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-30" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`kb-up-${k.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-30" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} data-testid={`kb-down-${k.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] shrink-0"><BookOpen className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[k.status] || STATUS_STYLE.draft}`}>{k.status || "draft"}</span>
                    <span className="text-[10px] uppercase font-semibold text-[hsl(var(--pc-gold))]">{k.category}</span>
                  </div>
                  <div className="font-semibold text-[hsl(var(--primary))] mt-0.5 truncate">{k.title}</div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2 whitespace-pre-line">{k.content}</p>
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--primary))] border border-[hsl(var(--pc-gold)/0.4)] px-2.5 py-1 text-[11px] font-medium"><Users className="h-3 w-3" /> {k.audience_label}{k.audience_count != null ? ` · ${k.audience_count}` : ""}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" data-testid={`kb-edit-${k.id}`} onClick={() => setEditing(k)}><Pencil className="h-3.5 w-3.5" /></Button>
                {k.status !== "published"
                  ? <Button size="sm" data-testid={`kb-publish-${k.id}`} onClick={() => act(() => api.mgrKbPublish(eventId, k.id, true), "Published")}><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>
                  : <Button size="sm" variant="outline" data-testid={`kb-unpublish-${k.id}`} onClick={() => act(() => api.mgrKbPublish(eventId, k.id, false), "Unpublished")}><EyeOff className="h-3.5 w-3.5 mr-1" /> Unpublish</Button>}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`kb-more-${k.id}`}><MoreVertical className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => act(() => api.mgrKbDuplicate(eventId, k.id), "Duplicated")}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                    {k.status !== "archived"
                      ? <DropdownMenuItem onClick={() => act(() => api.mgrKbArchive(eventId, k.id, true), "Archived")}><Archive className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>
                      : <DropdownMenuItem onClick={() => act(() => api.mgrKbArchive(eventId, k.id, false), "Restored")}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>}
                    {(k.status || "draft") === "draft" && <DropdownMenuItem className="text-red-600" onClick={() => act(() => api.mgrKbDelete(eventId, k.id), "Deleted")}><Trash2 className="h-4 w-4 mr-2" /> Delete draft</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <KbEditor eventId={eventId} article={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function KbEditor({ eventId, article, onClose, onSaved }) {
  const isNew = !article.id;
  const [f, setF] = useState({ ...blank(), ...article });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { category: f.category, title: f.title, content: f.content, cta_label: f.cta_label, cta_url: f.cta_url, audience_spec: f.audience_spec };
      if (isNew) await api.mgrKbCreate(eventId, body);
      else await api.mgrKbUpdate(eventId, article.id, body);
      toast.success(isNew ? "Article created" : "Article updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="kb-editor">
        <DialogHeader><DialogTitle>{isNew ? "Add Article" : "Edit Article"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Section</Label><Input data-testid="kb-category" value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Your Travel & Guest Policy" /></div>
            <div><Label>Title</Label><Input data-testid="kb-title" value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          </div>
          <div><Label>Body</Label><Textarea rows={8} data-testid="kb-content" value={f.content} onChange={(e) => set("content", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>CTA label (optional)</Label><Input value={f.cta_label} onChange={(e) => set("cta_label", e.target.value)} /></div>
            <div><Label>CTA link (optional)</Label><Input value={f.cta_url} onChange={(e) => set("cta_url", e.target.value)} /></div>
          </div>
          <AudiencePicker eventId={eventId} value={f.audience_spec} onChange={(s) => set("audience_spec", s)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="kb-save" onClick={save} disabled={saving}>{saving ? "Saving…" : (isNew ? "Create" : "Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
